import React, { ChangeEvent, useCallback, useState } from 'react';
import styles from './VideoForm.module.scss';
import parentStyles from '../EditContent.module.scss';
import Button from '../../../../../../components/button/Button';
import IconVideo from '../../../../../../assets/icons/content_type_video.svg?react';
import IconTrash from '../../../../../../assets/icons/trash_circle.svg?react';
import ContentVideo from '../../../../../../components/content-video/ContentVideo';
import SubtitlesList from '../subtitles-list/SubtitlesList';
import SubtitlesUploadModal from '../subtitles-upload-modal/SubtitlesUploadModal';
import { logger } from '../../../../../../utils/logger';
import { useVideoTranslation } from '@src/hooks/useVideoTranslation';
import { useToast } from '@src/hooks';
import { TRANSLATION_LANGUAGES } from '@types';

type Props = {
  data: string | undefined;
  meta?: string | null;
  dataFile?: File | undefined;
  onChange: (e: File | undefined) => void;
  isUploading?: boolean;
  uploadingPercent?: number;
};

const MuxDomain = 'https://stream.mux.com';

function bytesToSize(bytes: number) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
}

function VideoForm(props: Props) {
  const { data, meta, dataFile, onChange, isUploading, uploadingPercent } = props;

  const [showUploadSubtitles, setShowUploadSubtitles] = useState(false);

  const isUploaded = (data ?? '').startsWith(MuxDomain);
  const [, playbackId] = /([^/]+).m3u8$/.exec(data || '') ?? [];

  let assetId;

  const { successToast, errorToast } = useToast();

  const {
    generateEnglishCaptions,
    createTranslationJob,
    downloadEnglishSubtitles,
    downloadTranslationSubtitles,
    englishCaptionJob,
    translationJob,
    loading,
    error,
    isProcessing,
    overallProgress,
    reset,
  } = useVideoTranslation({
    pollingInterval: 3000,
    onEnglishCaptionsComplete: (job) => {
      successToast('English captions ready, Please download!');
    },
    onTranslationComplete: (job) => {
      successToast(`Translations completed, Please download!`);
    },
    onError: (error) => {
      errorToast(`Translation error, Please try again!`);
    },
  });

  if (meta) {
    try {
      assetId = JSON.parse(meta)?.id;
    } catch (e) {
      logger.debug(`[Can't parse meta info]`);
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      window.alert('No file selected');
    } else {
      onChange(file);
    }
  };

  const handleClear = () => onChange(undefined);

  const handleOpenUploadSubtitlesModal = useCallback(() => {
    setShowUploadSubtitles(true);
  }, [setShowUploadSubtitles]);

  const handleCloseUploadSubtitlesModal = useCallback(() => {
    setShowUploadSubtitles(false);
  }, [setShowUploadSubtitles]);

  const handleTranslateVideo = async () => {
    await generateEnglishCaptions(assetId);
  };

  const handleTranslateToLanguages = async (assetId: string, languages: string[] = []) => {
    await createTranslationJob(assetId, languages);
  };

  return (
    <>
      <div className={parentStyles.row}>
        <div className={styles.uploader}>
          <input accept={'video/*'} type="file" className={styles.fileInput} onChange={handleChange}/>
          <Button label={'Upload video'} color={'orange'} outline={true} className={styles.inputButton}
                  leftIcon={<IconVideo/>}/>
        </div>
      </div>
      {dataFile && <div className={styles.row}>
        <div className={styles.title}>{isUploading ? `Uploading: ${uploadingPercent}%` : 'Video (not uploaded yet)'}</div>
        <div className={styles.fileContent}>
          <div className={styles.fileTitle}>{dataFile.name} - {bytesToSize(dataFile.size)}</div>
          <div className={styles.fileRemoveWrapper} onClick={handleClear}>
            <IconTrash/>
          </div>
        </div>
      </div>}
      {isUploaded && <>
        <div className={styles.row}>
          <div className={styles.title}>Video</div>
          <div className={styles.videoWrapper}>
            <ContentVideo data={data!} autoPlay={false}/>
          </div>
        </div>
        <div className={styles.videoTranslation}>
          <Button disabled={loading || isProcessing} label={englishCaptionJob?.status === 'processing' ? `Processing... ${overallProgress}%` : 'Translate English Subtitle'} color='orange' onClick={handleTranslateVideo} className={styles.videoTranslateBtn}/>
          {englishCaptionJob && (
            <div className={styles.translationStatus}>
              <h3>English Captions</h3>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <strong>Progress: {englishCaptionJob.progressPercentage}%</strong>
                {englishCaptionJob.subtitleUrl && (
                  <Button label='Download English Subtitles' onClick={downloadEnglishSubtitles} size='small' color='green' className={styles.downloadBtn} />
                )}
              </div>
            </div>
          )}
          {
          <div style={{display: 'flex', gap: '12px'}}>
            <Button disabled={loading || isProcessing} label={translationJob?.status === "processing" ? `Processing... ${overallProgress}%` : 'Translate All Subtitles'} onClick={() => handleTranslateToLanguages(assetId, [])} className={styles.videoTranslateBtn}/>
            {translationJob?.status === "processing" && <Button label='Stop Translation' onClick={reset} color="red" className={styles.stopTranslateBtn}/>}
          </div>
          }
          {error && (
            <div style={{ color: 'red' }}>
              Error: {error}
              <Button onClick={reset} label='Retry' color='green' size='small' className={styles.retryTranslateBtn} />
            </div>
          )}

          {translationJob && translationJob.languageJobs && (
            <div>
              <div className={styles.translationStatus}>
                <h3>Translations</h3>
                <div><strong>Status:</strong>&nbsp;{translationJob.status}, &nbsp;<strong>Progress: </strong>&nbsp;{translationJob.progressPercentage}%</div>
              </div>
              <div className={styles.translationSubBlock}>
                {translationJob.languageJobs.map((languageJob) => (
                  <div key={languageJob.id} style={{ margin: '10px 0' }} className={styles.translationSubTitle}>
                    <div><strong>{TRANSLATION_LANGUAGES.find(lang => lang.code === languageJob.languageCode)?.name }:&nbsp;</strong> {languageJob.status}</div>
                    {languageJob.subtitleUrl && (
                      <Button onClick={() => downloadTranslationSubtitles(languageJob.languageCode)} label='Download Subtitles' size='small' color='green' className={styles.downloadBtn}/>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className={styles.row}>
          <div className={styles.titleWrapper}>
            <div className={styles.title}>Subtitles</div>
            <Button className={styles.uploadBtn} size={'extraSmall'} onClick={handleOpenUploadSubtitlesModal} outline={true} label={'Upload subtitles'} color={'orange'}/>
          </div>
          <SubtitlesList playbackId={playbackId} id={assetId} />
        </div>
      </>
      }
      {showUploadSubtitles && playbackId && <SubtitlesUploadModal playbackId={playbackId} id={assetId} onClose={handleCloseUploadSubtitlesModal}/>}
    </>
  );
}

export default VideoForm;
